import React, { useEffect, useMemo } from "react";
import ObjectHeading, { PinnedNotesConfig } from "@/components/ObjectHeading";
import { ProfileSkeleton } from "@/components/Skeleton";
import { useQuery } from "@apollo/client";
import { PERSON_DETAILS_HEADER_QUERY } from "../../queries";
import { Person, ProgramTypeName, Classification, CRMNote, NotePriorityValues, NoteNotableValues } from "@/types";
import { capitalize } from "lodash";
import { getProgramStatus, getPrimarySectionName } from "@/pages/people/_utils";
//import { toUTCFormat } from "@/lib/dateHelpers";
import { isInFuture, toFormat } from "@/lib/dateHelpers";
import {
  isNotTerminatedMemberOrAssociate,
  hasEmploymentEnded,
  shouldSuppressUnemployedForClassification,
  getActiveSuspension,
  getSuspensionReasonLabel
} from "../../utils/utils";
import Tag from "@/components/Tag";
import { TagType, TagStyle } from "@/components/Tag/Tag";
import { renderName } from "@/lib/personHelpers";
import { Action } from "@/components/MenuButton";
import { usePersonActions } from "../../hooks/usePersonActions";
import Tooltip from "@/components/Tooltip";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { withStickyHeader } from "@/components/StickyHeader";
import { useQuickActionMenusRedesign } from "@/hooks/useQuickActionMenusRedesign";
import { PERSON_NOTES_QUERY, PersonNotesQueryResponse } from "@/pages/people/[pgaId]/person/notes/queries";
import { useNavigate, useLocation } from "react-router-dom";
import { useHasSearchParam } from "@/hooks/useHasSearchParam";
import { getNoteParentLink, getNotesPagePath } from "@/features/global/components/NotesTable/noteHelpers";
import { usePhotoboothOptional } from "@/features/photobooth";
import { useCanAccessPhotoboothBool } from "@/hooks/useCapabilities";
/*import { gql } from "@apollo/client";

// TODOQuery to fetch all classifications with descriptions
const CLASSIFICATIONS_QUERY = gql`
  query GetClassifications {
    classifications {
      nodes {
        id
        name
        description
      }
    }
  }
`;*/

type ObjectHeadingWrapper = {
  personData?: { person: Person };
  pgaId: string;
  actionButtons?: Action[];
  menuButtonLabel?: string;
  isDetailsViewDesktop?: boolean;
  showMinimalMetadata?: boolean; // New prop to control metadata display
  avatarSize?: "medium" | "large" | "xlarge"; // New prop to control avatar size (small not used in practice)
  variant?: "complete" | "condensed" | "landing-page"; // New prop to control ObjectHeading variant
  hideTags?: boolean; // New prop to hide status tags
  onImageEditClick?: () => void; // New prop to enable photo editing
  pinnedNotes?: PinnedNotesConfig;
  isSticky?: boolean;
  initialNotesDialogOpen?: boolean;
};

type PersonData = { person: Person };

const createProgramAndSectionInfo = (person: Person) => {
  const { latestProgram, membershipSince } = person;
  const primarySectionName = getPrimarySectionName(person);

  // If no program, just show section info if available
  if (!latestProgram) {
    return primarySectionName ? [{ text: primarySectionName, boldValue: true }] : [];
  }

  const { programType, band } = latestProgram;

  // Create program info text
  const programText = membershipSince
    ? `${programType.name}` // (${toUTCFormat(membershipSince, "yyyy")})
    : programType.name;

  // For students, combine with university name; for other non-prospects, combine with section name
  let combinedText = programText;

  if (programType.name === ProgramTypeName.Student) {
    const universityName = band?.section?.name;
    combinedText = universityName ? `${programText}, ${universityName}` : programText;
  } else if (programType.name !== ProgramTypeName.Prospect && primarySectionName) {
    combinedText = `${programText}, ${primarySectionName}`;
  }

  return [{ text: combinedText, boldValue: true }];
};

const createClassificationInfo = (person: Person, classifications?: Classification[]) => {
  const { latestProgram, employments } = person;

  // For students, don't show Job or Class metadata
  if (latestProgram?.programType.name === ProgramTypeName.Student) {
    return [];
  }

  // Check for active employment first (no endedOn date)
  const primaryEmployment = employments?.find((e) => e.isPrimary);
  const hasActiveEmployment = primaryEmployment && !primaryEmployment.endedOn;

  if (hasActiveEmployment) {
    // Find matching classification by name
    const matchingClassification = classifications?.find(
      (classification) => classification.name === primaryEmployment.jobClassification
    );

    const displayTitle = primaryEmployment.formattedTitle || primaryEmployment.title;
    const jobClassification = primaryEmployment.jobClassification;

    if (!displayTitle || !jobClassification) {
      return [];
    }

    return [
      {
        text: `${displayTitle} (${jobClassification})`,
        label: "Job",
        tooltipText: matchingClassification?.description || displayTitle
      }
    ];
  }

  // Check for active membership band if no active employment (band not expired)
  const hasActiveBand =
    latestProgram?.band?.classification && latestProgram?.band?.expiresOn && isInFuture(latestProgram.band.expiresOn);

  if (hasActiveBand && latestProgram.band) {
    const { band, programType } = latestProgram;
    const { name, description } = band.classification;

    return [
      {
        text: `${name} (${programType.name})`,
        label: "Class",
        tooltipText: description
      }
    ];
  }

  // If neither active employment nor active band, hide altogether
  return [];
};

const createFacilityInfo = (person: Person) => {
  // For students, don't show facility information
  if (person.latestProgram?.programType.name === ProgramTypeName.Student) {
    return [];
  }

  const primaryEmployment = person.employments.find((e) => e.isPrimary);

  if (!primaryEmployment) {
    // Only show unemployed status for active/suspended Members/Associates
    if (isNotTerminatedMemberOrAssociate(person)) {
      if (shouldSuppressUnemployedForClassification(person)) {
        return [];
      }
      // Try to find the most recently ended employment for tooltip info
      const mostRecentEndedEmployment = [...person.employments]
        .filter((e) => hasEmploymentEnded(e))
        .sort((a, b) => {
          if (!a.endedOn || !b.endedOn) return 0;
          return new Date(b.endedOn).getTime() - new Date(a.endedOn).getTime();
        })[0];

      const tooltipText = mostRecentEndedEmployment?.endedOn
        ? `Employment Ended ${toFormat(mostRecentEndedEmployment.endedOn)}`
        : undefined;

      return [{ text: "Unemployed", label: "Facility", tooltipText }];
    }
    return [];
  }

  if (!primaryEmployment.facility) return [];

  const { facility } = primaryEmployment;
  const facilityName = facility.name;

  // Get the main address or first available address
  const mainAddress = facility.addresses?.find((addr) => addr.addressType === "main") || facility.addresses?.[0];

  let facilityText = facilityName;
  if (mainAddress?.city && mainAddress?.state) {
    facilityText = `${facilityName}, ${mainAddress.city}, ${mainAddress.state}`;
  }

  return [{ text: facilityText, label: "Facility" }];
};

const hasActiveClassFBand = (person: Person): boolean => {
  const { latestProgram } = person;
  const band = latestProgram?.band;

  return !!(
    band?.classification?.name === "F" &&
    (latestProgram?.status.includes("ACTIVE") || latestProgram?.status.includes("SUSPENDED"))
  );
};

export const ProgramStatusTag = ({
  status,
  terminatedReason,
  terminatesOn,
  suspensionReason,
  suspensionStartDate,
  pendingTerminatesOn
}: {
  status: string;
  terminatedReason?: string | null;
  terminatesOn?: string | null;
  suspensionReason?: string | null;
  suspensionStartDate?: string | null;
  pendingTerminatesOn?: string | null;
}) => {
  const isDeceased = terminatedReason?.toLowerCase() === "deceased";
  const isSuspended = status === "Suspended";
  const isTerminated = status === "Terminated" && !isDeceased;
  const isPending = status === "Pending";

  const statusConfig: Record<string, { tagType: TagType; tagStyle: TagStyle }> = {
    Active: {
      tagType: "success",
      tagStyle: "strong"
    },
    Pending: {
      tagType: "warn",
      tagStyle: "strong"
    },
    Inactive: { tagType: "surface", tagStyle: "strong" },
    Suspended: { tagType: "warn", tagStyle: "strong" },
    Terminated: isDeceased ? { tagType: "surface", tagStyle: "strong" } : { tagType: "error", tagStyle: "strong" }
  };

  const config = statusConfig[status as keyof typeof statusConfig];
  if (!config) return null;

  const displayText = isDeceased ? "Deceased" : capitalize(status);

  const formatReasonWithDate = (reason: string, date?: string | null) =>
    date ? `${reason} on ${toFormat(date, "MMM dd, yyyy")}` : reason;

  const tooltipText =
    isDeceased && terminatesOn
      ? formatReasonWithDate("Deceased", terminatesOn)
      : isPending && pendingTerminatesOn
        ? formatReasonWithDate("Pending registration terminates", pendingTerminatesOn)
        : isSuspended && suspensionReason
          ? formatReasonWithDate(suspensionReason, suspensionStartDate)
          : isTerminated && terminatedReason
            ? formatReasonWithDate(capitalize(terminatedReason.replace(/_/g, " ").toLowerCase()), terminatesOn)
            : undefined;

  const iconName = isDeceased
    ? "info"
    : isPending && tooltipText
      ? "info"
      : (isSuspended || isTerminated) && tooltipText
        ? "error"
        : undefined;

  if (tooltipText) {
    return (
      <Tooltip
        variant="plain"
        supportingText={tooltipText}
        color="black"
        menuCorner="end-start"
        anchorCorner="start-start"
        positioning="popover"
        anchorClassName="p-1 -m-1"
      >
        <Tag tagType={config.tagType} tagStyle={config.tagStyle} size="small" iconName={iconName}>
          {displayText}
        </Tag>
      </Tooltip>
    );
  }

  return (
    <Tag tagType={config.tagType} tagStyle={config.tagStyle} size="small" iconName={iconName}>
      {displayText}
    </Tag>
  );
};

export const CLASS_F_TAG_TOOLTIP = "Didn't meet last cycle's PDR requirements";

export const ClassFTag = () => {
  return (
    <Tooltip
      variant="plain"
      supportingText={CLASS_F_TAG_TOOLTIP}
      color="black"
      menuCorner="end-start"
      anchorCorner="start-start"
      positioning="popover"
      anchorClassName="p-1 -m-1"
    >
      <Tag tagType="error" size="small" iconName="info">
        Class F
      </Tag>
    </Tooltip>
  );
};

const buildPersonTags = (person: Person, hideTags: boolean): React.ReactNode[] | undefined => {
  if (hideTags) return;

  const activeSuspension = getActiveSuspension(person.ccmsSuspensions);
  const suspensionReason = activeSuspension ? getSuspensionReasonLabel(activeSuspension) : undefined;
  const programStatus = getProgramStatus(person);
  const tags: React.ReactNode[] = [];

  if (programStatus) {
    tags.push(
      <ProgramStatusTag
        key="status"
        status={programStatus}
        terminatedReason={person.latestProgram?.band?.terminatedReason}
        terminatesOn={person.latestProgram?.band?.terminatesOn}
        suspensionReason={suspensionReason}
        suspensionStartDate={activeSuspension?.startDate}
        pendingTerminatesOn={person.latestProgram?.band?.expiresOn}
      />
    );
  }

  if (hasActiveClassFBand(person)) {
    tags.push(<ClassFTag key="class-f" />);
  }

  return tags.length ? tags : undefined;
};

export const ObjectHeadingWrapper = ({
  pgaId,
  actionButtons,
  menuButtonLabel,
  isDetailsViewDesktop,
  personData,
  showMinimalMetadata = false,
  avatarSize,
  variant,
  hideTags = false,
  onImageEditClick,
  pinnedNotes,
  isSticky,
  initialNotesDialogOpen
}: ObjectHeadingWrapper) => {
  const { data, loading, error } = useQuery<PersonData>(PERSON_DETAILS_HEADER_QUERY, {
    variables: { pgaId },
    skip: !!personData,
    errorPolicy: "all" // Don't throw, just return error - let parent handle it
  });

  // TODO: Re-enable after adding proper test mocks
  // const { data: classificationsData } = useQuery<{
  //   classifications: { nodes: Classification[] };
  // }>(CLASSIFICATIONS_QUERY, {
  //   errorPolicy: "ignore",
  //   skip: !(personData || data)
  // });
  const classificationsData /*: { classifications: { nodes: Classification[] } } | undefined */ = undefined;

  const { isMobile } = useIsCompactLayout();

  // For condensed variant or mobile-only (< 768px), automatically show minimal metadata (just PGA ID)
  // Tablets (768-1023px) show full metadata
  const effectiveShowMinimalMetadata = showMinimalMetadata || variant === "condensed" || (!variant && isMobile);

  if (loading) return <ProfileSkeleton />;
  if (error) return null; // Let parent component handle the error

  const { person } = personData || data!;
  // Determine appropriate avatar size based on variant or auto-detection
  const effectiveAvatarSize = avatarSize || "large";
  const { profilePhoto, displayName, informalName, preferredLastName, suffixName, middleName, firstName } = person;
  const classifications = classificationsData;

  const metaData = effectiveShowMinimalMetadata
    ? [{ text: pgaId, label: "PGA ID", copyable: true }] // Only show PGA ID for condensed variant or minimal metadata
    : [
        ...createProgramAndSectionInfo(person),
        { text: pgaId, label: "PGA ID", copyable: true },
        ...createFacilityInfo(person),
        ...createClassificationInfo(person, classifications)
      ];

  const title = renderName(displayName, informalName, preferredLastName, suffixName, middleName, firstName);

  return (
    <ObjectHeading
      title={title}
      imageUrl={profilePhoto}
      metaData={metaData}
      iconName="person"
      actions={actionButtons}
      menuButtonLabel={menuButtonLabel}
      isDetailsView
      tag={buildPersonTags(person, hideTags)}
      isDetailsViewDesktop={isDetailsViewDesktop}
      avatarSize={effectiveAvatarSize}
      variant={variant}
      onImageEditClick={onImageEditClick}
      pinnedNotes={pinnedNotes}
      isSticky={isSticky}
      initialNotesDialogOpen={initialNotesDialogOpen}
    />
  );
};

type Props = {
  pgaId: string;
  hideActions?: boolean;
  hideTags?: boolean;
  avatarSize?: "medium" | "large" | "xlarge";
  isSticky?: boolean;
  disableScrollAdjustment?: boolean;
  disableSticky?: boolean;
  onImageEditClick?: () => void;
};

const VISITED_PERSON_PROFILES_KEY = "visitedPersonProfiles";
const STUDENT_NOTABLE_TYPES: string[] = [
  NoteNotableValues.UNIVERSITY_COHORT_ENROLLMENT,
  NoteNotableValues.UNIVERSITY_PROBATION
];

// Base PersonObjectHeading component (non-sticky)
function BasePersonObjectHeading({
  pgaId,
  hideActions = false,
  hideTags = false,
  avatarSize,
  isSticky,
  onImageEditClick
}: Props) {
  const { actionButtons, menuButtonLabel, personData } = usePersonActions(pgaId);
  const { isCompactLayout, isMobile } = useIsCompactLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const hasNoteDeepLink = useHasSearchParam("openNote");

  const { data: notesData } = useQuery<PersonNotesQueryResponse>(PERSON_NOTES_QUERY, {
    variables: { pgaId }
  });

  // Active student university only — lastAttended must not feed getNoteParentLink,
  // which builds student-detail URLs that StudentDetailLayout queries with alumni: false.
  const universityLegacyId = personData?.university?.legacyId;

  const viewAllNotesPath = getNotesPagePath(pgaId, location.pathname);
  const studentRouteMatch = location.pathname.match(/^\/education\/universities\/(\d+)\/students\/[^/]+/);

  const filteredNotes = useMemo(
    () =>
      notesData?.personNotes?.nodes?.filter((note) => {
        if (note.priority === "NONE") return false;
        if (studentRouteMatch && !STUDENT_NOTABLE_TYPES.includes(note.notableType)) return false;
        return true;
      }) || [],
    [notesData, studentRouteMatch]
  );

  const hasAlertNotes = useMemo(() => {
    return filteredNotes.some((note) => note.priority === NotePriorityValues.ALERT);
  }, [filteredNotes]);

  const shouldAutoOpen = useMemo(() => {
    if (!hasAlertNotes || !notesData || hasNoteDeepLink) {
      return false;
    }
    try {
      const stored = sessionStorage.getItem(VISITED_PERSON_PROFILES_KEY);
      const visited = stored ? JSON.parse(stored) : [];
      return !visited.includes(pgaId);
    } catch {
      return false;
    }
  }, [hasAlertNotes, notesData, pgaId, hasNoteDeepLink]);

  useEffect(() => {
    if (notesData && shouldAutoOpen) {
      try {
        const stored = sessionStorage.getItem(VISITED_PERSON_PROFILES_KEY);
        const visited = stored ? JSON.parse(stored) : [];
        if (!visited.includes(pgaId)) {
          sessionStorage.setItem(VISITED_PERSON_PROFILES_KEY, JSON.stringify([...visited, pgaId]));
        }
      } catch {
        // Ignore sessionStorage errors
      }
    }
  }, [pgaId, notesData, shouldAutoOpen]);

  const pinnedNotes: PinnedNotesConfig | undefined = {
    notes: filteredNotes,
    onViewAll: () => navigate(viewAllNotesPath),
    getNoteAction: (note: CRMNote) => {
      const link = getNoteParentLink(note, { pgaId, universityLegacyId });
      return link ? () => navigate(link) : undefined;
    }
  };

  const photoboothContext = usePhotoboothOptional();
  const canAccessPhotobooth = useCanAccessPhotoboothBool();

  const effectiveOnImageEditClick =
    onImageEditClick ||
    (canAccessPhotobooth && photoboothContext
      ? () => photoboothContext.openPhotoboothByPgaId(pgaId, { source: "person_avatar" })
      : undefined);
  const effectiveHideActions = hideActions || (isMobile && isSticky);
  const effectiveHideTags = hideTags || (isMobile && isSticky);
  const effectiveAvatarSize = avatarSize || (isCompactLayout ? "medium" : "large");

  return (
    <div data-testid="person-details-header">
      <ObjectHeadingWrapper
        pgaId={pgaId}
        actionButtons={effectiveHideActions ? [] : actionButtons}
        menuButtonLabel={effectiveHideActions ? undefined : menuButtonLabel}
        personData={personData ? { person: personData } : undefined}
        avatarSize={effectiveAvatarSize}
        hideTags={effectiveHideTags}
        onImageEditClick={effectiveOnImageEditClick}
        isSticky={isSticky}
        pinnedNotes={pinnedNotes}
        initialNotesDialogOpen={shouldAutoOpen}
      />
    </div>
  );
}

export const PERSON_OBJECT_HEADING_MOBILE_BASE_OFFSET_REM = 8.25;
export const PERSON_OBJECT_HEADING_COMPACT_NAVBAR_MOBILE_BASE_OFFSET_REM = 7;

// Default sticky version with scroll adjustment
const DefaultPersonObjectHeading = withStickyHeader(BasePersonObjectHeading, {
  baseOffset: 4.75, // Desktop - below TopActionBar
  mobileBaseOffset: PERSON_OBJECT_HEADING_MOBILE_BASE_OFFSET_REM, // Mobile - below TopActionBar + Breadcrumb compact bar
  scrollAdjustment: 0.5,
  backgroundColor: "bg-sys-brand-surface"
});

const CompactNavbarRedesignPersonObjectHeading = withStickyHeader(BasePersonObjectHeading, {
  baseOffset: 4.75,
  mobileBaseOffset: PERSON_OBJECT_HEADING_COMPACT_NAVBAR_MOBILE_BASE_OFFSET_REM, // Mobile - below compact TopActionBar + Breadcrumb compact bar
  scrollAdjustment: 0.5,
  backgroundColor: "bg-sys-brand-surface"
});

// Version without scroll adjustment for FocusedPageLayout contexts
const NoScrollAdjustmentPersonObjectHeading = withStickyHeader(BasePersonObjectHeading, {
  baseOffset: 6.6875, // Desktop - below FocusedPageLayout TopAppBar (107px = pt-7[28px] + h-[70px] + pb-2[8px] + border-b[1px])
  mobileBaseOffset: 6.6875, // Mobile - same as desktop for FocusedPageLayout
  scrollAdjustment: 0, // No scroll adjustment to prevent collision
  backgroundColor: "bg-sys-brand-surface"
});

// Wrapper that chooses the appropriate version
function PersonObjectHeadingWrapper(props: Props) {
  const { disableScrollAdjustment, disableSticky, ...componentProps } = props;
  const compactNavbarRedesign = useQuickActionMenusRedesign();

  if (disableSticky) {
    return <BasePersonObjectHeading {...componentProps} isSticky={false} />;
  }

  if (disableScrollAdjustment) {
    return <NoScrollAdjustmentPersonObjectHeading {...componentProps} />;
  }

  const Component = compactNavbarRedesign ? CompactNavbarRedesignPersonObjectHeading : DefaultPersonObjectHeading;
  return <Component {...componentProps} />;
}

// Export the wrapper as PersonObjectHeading
export const PersonObjectHeading = PersonObjectHeadingWrapper;
